import { useState } from 'react';
import type { Tenant, OpeningDay } from '../../lib/database.types';
import {
  normalizeOpeningHours,
  setDayOpen,
  addSlot,
  updateSlot,
  removeSlot,
  validateOpeningHours,
} from '../../lib/tenantSettings';
import { saveTenantHours } from '../../lib/tenantSettingsService';

/**
 * Section « Horaires » de l'admin (parité legacy Hours tab) — éditeur isolé :
 * durée de créneau, ouverture/fermeture par jour avec services multiples,
 * capacité par créneau, fermetures exceptionnelles. Enregistré via RPC save_tenant_hours.
 */
export default function HoursManager({ tenant }: { tenant: Tenant }) {
  const [days, setDays] = useState<OpeningDay[]>(() => normalizeOpeningHours(tenant.opening_hours ?? null));
  const [stepMin, setStepMin] = useState(tenant.slot_step_min ?? 30);
  const [capacity, setCapacity] = useState(tenant.slot_capacity ?? 5);
  const [closures, setClosures] = useState<string[]>(
    (tenant.closures ?? [])
      .map((c) => (typeof c === 'string' ? c : c?.date ?? ''))
      .filter((s): s is string => Boolean(s)),
  );
  const [newClosure, setNewClosure] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const errors = validateOpeningHours(days);

  const save = async () => {
    if (errors.length > 0) {
      setMsg({ tone: 'err', text: errors[0] });
      return;
    }
    setBusy(true);
    setMsg(null);
    const r = await saveTenantHours({ tenantId: tenant.id, openingHours: days, closures, stepMin, capacity });
    setBusy(false);
    setMsg(r.ok ? { tone: 'ok', text: 'Horaires enregistrés.' } : { tone: 'err', text: r.error || 'Erreur.' });
  };

  return (
    <div className="flex flex-col gap-5">
      {/* En-tête + durée créneau + sauvegarde */}
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div>
          <h3 className="text-base font-bold">Horaires d'ouverture</h3>
          <p className="text-sm text-slate-500">Les créneaux de commande sont générés à partir de ces horaires.</p>
        </div>
        <div className="flex items-end gap-3">
          <label className="flex flex-col text-sm">
            <span className="mb-1 text-xs font-semibold opacity-60">Durée d'un créneau</span>
            <select
              value={stepMin}
              onChange={(e) => setStepMin(parseInt(e.target.value, 10))}
              className="rounded-lg border border-slate-200 p-2"
            >
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={60}>1 heure</option>
            </select>
          </label>
          <button
            onClick={save}
            disabled={busy}
            className="rounded-xl px-5 py-2.5 font-semibold text-white disabled:opacity-40"
            style={{ background: 'var(--brand-primary)' }}
          >
            {busy ? 'Enregistrement…' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Jours */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-2">
          {days.map((d) => (
            <div key={d.day} className="rounded-lg border border-slate-100 p-2">
              <div className="mb-1 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input type="checkbox" checked={d.open} onChange={(e) => setDays((cur) => setDayOpen(cur, d.day, e.target.checked))} />
                  {d.day}
                </label>
                {d.open ? (
                  <button onClick={() => setDays((cur) => addSlot(cur, d.day))} className="text-xs" style={{ color: 'var(--brand-primary)' }}>
                    + créneau
                  </button>
                ) : (
                  <span className="text-xs opacity-50">Fermé</span>
                )}
              </div>
              {d.open &&
                d.slots.map((s, i) => (
                  <div key={i} className="mb-1 flex items-center gap-2">
                    <input type="time" value={s[0]} onChange={(e) => setDays((cur) => updateSlot(cur, d.day, i, 0, e.target.value))} className="rounded border border-slate-200 px-2 py-1 text-sm" />
                    <span className="opacity-50">→</span>
                    <input type="time" value={s[1]} onChange={(e) => setDays((cur) => updateSlot(cur, d.day, i, 1, e.target.value))} className="rounded border border-slate-200 px-2 py-1 text-sm" />
                    <button onClick={() => setDays((cur) => removeSlot(cur, d.day, i))} className="text-sm text-slate-400" title="Supprimer ce créneau">✕</button>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>

      {/* Capacité */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-base font-bold">Capacité par créneau</h3>
        <div className="flex items-center gap-2">
          <input type="number" min={1} value={capacity} onChange={(e) => setCapacity(Math.max(1, parseInt(e.target.value || '5', 10)))} className="w-24 rounded border border-slate-200 px-2 py-1" />
          <span className="text-sm text-slate-500">commandes par créneau</span>
        </div>
      </div>

      {/* Fermetures exceptionnelles */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-base font-bold">Fermetures exceptionnelles</h3>
        <ul className="mb-2 flex flex-col gap-1">
          {closures.map((c) => (
            <li key={c} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm">
              <span>📅 {c}</span>
              <button onClick={() => setClosures((arr) => arr.filter((x) => x !== c))} className="text-slate-400 hover:text-red-600" title="Retirer">🗑️</button>
            </li>
          ))}
          {closures.length === 0 && <li className="text-sm opacity-50">Aucune fermeture programmée.</li>}
        </ul>
        <div className="flex items-center gap-2">
          <input type="date" value={newClosure} onChange={(e) => setNewClosure(e.target.value)} className="rounded border border-slate-200 px-2 py-1 text-sm" />
          <button
            onClick={() => {
              if (newClosure && !closures.includes(newClosure)) setClosures((arr) => [...arr, newClosure].sort());
              setNewClosure('');
            }}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50"
          >
            + Ajouter
          </button>
        </div>
      </div>

      {errors.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-red-600">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
      {msg && <p className={`text-sm ${msg.tone === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{msg.text}</p>}
    </div>
  );
}
